import { MapLocation } from '@/types/location';
import { useLanguage } from "@/contexts/LanguageContext";
import { Monitor, Users, Layers, Ruler, Tag, GraduationCap, BookOpen, UserCheck, AlignLeft, DollarSign } from 'lucide-react';

interface PopupDetailsProps {
  location: MapLocation;
}

interface DetailRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}

const DetailRow = ({ icon, label, value }: DetailRowProps) => (
  <div className="flex items-center gap-3 py-2 border-b border-border/30 last:border-0">
    <div className="w-6 h-6 rounded-lg bg-muted/60 flex items-center justify-center flex-shrink-0">
      {icon}
    </div>
    <span className="text-sm text-muted-foreground flex-1 min-w-0">{label}</span>
    <span className="text-sm font-medium text-foreground text-right">{value}</span>
  </div>
);

export const PopupDetails = ({ location }: PopupDetailsProps) => {
  const { t, language } = useLanguage();
  const isAttentionLocation = location.mediaOwner === 'Attention';
  const isBridgeLocation = location.type === 'Digital Bridge Screen';
  const isOutdoorBillboard = location.type === 'Outdoor Billboard';

  const priceMatch = location.details?.description?.match(/Price:\s*([\d,.]+ SAR per (?:screen|face) per day)/);
  let priceInfo = priceMatch ? priceMatch[1] : null;

  if (priceInfo && language === 'ar') {
    priceInfo = priceInfo
      .replace('SAR per screen per day', t("sarPerScreenPerDay"))
      .replace('SAR per face per day', t("sarPerFacePerDay"));
  }

  const cleanDescription = location.details?.description?.replace(/\nPrice:.*/, '')?.replace(/\s*\|\s*Price:.*/, '');

  const getLocalizedLocationType = (type: string) => {
    if (language === 'ar') {
      switch (type) {
        case 'Digital Bridge Screen': return t("digitalBridgeScreen");
        case 'Outdoor Billboard': return t("outdoorBillboard");
        default: return type;
      }
    }
    return type;
  };

  const getLocalizedValue = (field: string, value: string) => {
    if (language === 'ar') {
      const lowerValue = value.toLowerCase();
      switch (field) {
        case 'gender':
          if (lowerValue === 'male') return t("male");
          if (lowerValue === 'female') return t("female");
          if (lowerValue === 'mixed') return t("mixed");
          break;
        case 'orientation':
          if (lowerValue === 'landscape') return t("landscape");
          if (lowerValue === 'portrait') return t("portrait");
          break;
      }
    }
    return value;
  };

  const iconClass = "h-3 w-3 text-muted-foreground";

  const mediaKindChipClass = location.mediaKind === 'Outdoor'
    ? 'bg-accent/30 border-accent/40 text-accent-foreground'
    : 'bg-secondary/60 border-secondary/80 text-secondary-foreground';

  return (
    <div className="space-y-3">
      {/* Stat chips row */}
      <div className="flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-primary/10 border border-primary/20 text-primary text-xs font-semibold">
          <Monitor className="h-3 w-3" />
          {location.devices} {location.devices === 1 ? t("screen") : t("screens")}
        </span>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-semibold border ${mediaKindChipClass}`}>
          <Layers className="h-3 w-3" />
          {location.mediaKind}
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-muted/70 border border-border/40 text-muted-foreground text-xs font-medium">
          <Tag className="h-3 w-3" />
          {location.screenType}
        </span>
      </div>

      {/* Price block */}
      {isAttentionLocation && priceInfo && (
        <div className="bg-primary/8 border border-primary/20 rounded-xl px-3 py-2.5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
              <DollarSign className="h-3 w-3 text-primary" />
            </div>
            <span className="text-sm font-bold text-primary">
              {t("cost")}: {priceInfo}
            </span>
          </div>
        </div>
      )}

      {/* Tech spec block for Bridge/Outdoor */}
      {(isBridgeLocation || isOutdoorBillboard) && (
        <div className="bg-primary/8 border border-primary/20 rounded-xl px-3 py-2.5 space-y-1.5">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
              <Monitor className="h-3 w-3 text-primary" />
            </div>
            <span className="text-sm font-bold text-primary">{t("outdoorLedDisplay")}</span>
          </div>
          <p className="text-xs text-muted-foreground ml-8">• {t("pixelSizes")}</p>
          <p className="text-xs text-muted-foreground ml-8">• {t("highBrightness")}</p>
        </div>
      )}

      {/* Detail rows */}
      <div className="rounded-xl bg-muted/30 border border-border/40 px-3 py-1">
        <DetailRow
          icon={<Tag className={iconClass} />}
          label={t("locationType")}
          value={getLocalizedLocationType(location.type)}
        />
        {location.details?.screenDimensions && (
          <DetailRow
            icon={<Ruler className={iconClass} />}
            label={t("screenSize")}
            value={location.details.screenDimensions}
          />
        )}
        {location.gender && (
          <DetailRow
            icon={<Users className={iconClass} />}
            label={t("gender")}
            value={getLocalizedValue('gender', location.gender)}
          />
        )}
        {location.ageRange && (
          <DetailRow
            icon={<UserCheck className={iconClass} />}
            label={t("ageRange")}
            value={location.ageRange}
          />
        )}
        {location.orientation && (
          <DetailRow
            icon={<Monitor className={iconClass} />}
            label={t("orientation")}
            value={getLocalizedValue('orientation', location.orientation)}
          />
        )}
        {location.segment && (
          <DetailRow
            icon={<Layers className={iconClass} />}
            label={t("segment")}
            value={location.segment}
          />
        )}
        {location.details?.schoolType && (
          <DetailRow
            icon={<BookOpen className={iconClass} />}
            label={t("schoolType")}
            value={location.details.schoolType}
          />
        )}
        {location.details?.level && (
          <DetailRow
            icon={<GraduationCap className={iconClass} />}
            label={t("educationLevel")}
            value={location.details.level}
          />
        )}
        {location.details?.students && (
          <DetailRow
            icon={<Users className={iconClass} />}
            label={t("students")}
            value={location.details.students}
          />
        )}
      </div>

      {/* Description block */}
      {cleanDescription && (
        <div className="bg-muted/40 border border-border/40 rounded-xl p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <AlignLeft className="h-3 w-3" />
            {t("description")}
          </div>
          <p className="text-xs text-foreground/80 leading-relaxed">{cleanDescription}</p>
        </div>
      )}
    </div>
  );
};
